/**
 * Lot 322 — CMS Preview Bridge (+ LOT 411 click-to-edit hooks)
 * ────────────────────────────────
 * Installe un pont `postMessage` entre l'iframe de la Live Preview
 * (SiteEditor admin) et le rendu front public / portail.
 *
 * Quand ce hook détecte que la page est rendue :
 *   - dans une iframe (window.self !== window.top)
 *   - AVEC un paramètre d'URL `_preview` (fourni par SiteEditorLivePreview),
 * il :
 *   1. Émet un message `cms:ready` vers le parent avec la liste des
 *      sections disponibles (repérées via `[data-cms-section]`).
 *   2. Écoute les messages `cms:highlight` envoyés par le parent,
 *      puis scrolle en douceur vers la section correspondante et
 *      applique la classe CSS `cms-preview-highlight` pendant 2.2 s.
 *   3. Désactive la capture des clics/scroll pour éviter toute
 *      pollution pendant l'aperçu (optionnel, bascule facile).
 *
 * LOT 411 — Click-to-edit (`?_cms_edit=1`) :
 *   • Détecte le mode edit (query string)
 *   • Émet `cms:edit-ready` avec la liste des paths éditables découverts
 *     (`[data-cms-field]`).
 *   • Écoute `cms:apply-draft` pour repeindre les valeurs en cours
 *     d'édition (utile quand l'utilisateur modifie une valeur dans la
 *     sidebar et qu'on veut la voir immédiatement dans l'iframe).
 *
 * Origine strictement vérifiée : seul le même origine que l'iframe
 * est autorisée à envoyer des commandes (garantit qu'aucun script
 * tiers ne peut manipuler le DOM en mode preview).
 *
 * Aucun impact sur le rendu public normal : si pas d'iframe +
 * pas de query `_preview`, le hook ne fait rien.
 */
import { useEffect } from 'react';

const HIGHLIGHT_CLASS = 'cms-preview-highlight';
const HIGHLIGHT_DURATION_MS = 2200;

export function isInPreview() {
  if (typeof window === 'undefined') return false;
  try {
    const isFramed = window.self !== window.top;
    const hasFlag = /(?:^|[?&])_preview=/.test(window.location.search);
    return isFramed && hasFlag;
  } catch {
    return /(?:^|[?&])_preview=/.test(window.location.search);
  }
}

export function isInCmsEdit() {
  if (typeof window === 'undefined') return false;
  try {
    const isFramed = window.self !== window.top;
    const hasEdit = /(?:^|[?&])_cms_edit=1/.test(window.location.search);
    return isFramed && hasEdit;
  } catch {
    return /(?:^|[?&])_cms_edit=1/.test(window.location.search);
  }
}

function collectSections() {
  const nodes = document.querySelectorAll('[data-cms-section]');
  return Array.from(nodes).map((el) => el.getAttribute('data-cms-section')).filter(Boolean);
}

function collectEditableFields() {
  const nodes = document.querySelectorAll('[data-cms-field]');
  const fields = [];
  nodes.forEach((el) => {
    const path = el.getAttribute('data-cms-field');
    if (!path) return;
    fields.push({
      path,
      multiline: el.tagName === 'P' || el.tagName === 'DIV'
        || el.getAttribute('data-cms-editable') === 'true',
      text: (el.innerText || '').slice(0, 200),
      tag: el.tagName.toLowerCase(),
    });
  });
  return fields;
}

function applyDraftField(path, value) {
  const els = document.querySelectorAll(`[data-cms-field="${CSS.escape(path)}"]`);
  els.forEach((el) => {
    if (el.innerText !== value) {
      // Preserve cursor when actively edited
      if (document.activeElement !== el) {
        el.innerText = value;
      }
    }
  });
}

function findSectionEl(key) {
  if (!key) return null;
  return document.querySelector(`[data-cms-section="${CSS.escape(key)}"]`);
}

let highlightTimer = null;
function highlightSection(key, { scroll = true } = {}) {
  // Clear existing highlight
  document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => {
    el.classList.remove(HIGHLIGHT_CLASS);
  });
  if (highlightTimer) {
    clearTimeout(highlightTimer);
    highlightTimer = null;
  }

  const el = findSectionEl(key);
  if (!el) return false;

  if (scroll) {
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch {
      el.scrollIntoView();
    }
  }
  // Force reflow so the animation restarts cleanly
  // eslint-disable-next-line no-unused-expressions
  void el.offsetWidth;
  el.classList.add(HIGHLIGHT_CLASS);
  highlightTimer = setTimeout(() => {
    el.classList.remove(HIGHLIGHT_CLASS);
    highlightTimer = null;
  }, HIGHLIGHT_DURATION_MS);
  return true;
}

export function useCmsPreviewBridge() {
  useEffect(() => {
    const previewMode = isInPreview();
    const editMode = isInCmsEdit();
    if (!previewMode && !editMode) return undefined;

    // Ambient mode classes (so CSS can react)
    if (previewMode) document.documentElement.classList.add('cms-preview-active');
    if (editMode) document.documentElement.classList.add('cms-edit-active');

    const postToParent = (payload) => {
      try {
        window.parent?.postMessage(payload, window.location.origin);
      } catch {
        /* ignore */
      }
    };

    // Signaler qu'on est prêt + envoyer la cartographie des sections (et des
    // champs éditables si en mode edit).
    const announceReady = () => {
      const payload = {
        type: 'cms:ready',
        sections: collectSections(),
        href: window.location.href,
      };
      if (editMode) {
        payload.fields = collectEditableFields();
      }
      postToParent(payload);
    };

    // Un premier "ready" dès que le DOM est peint, puis un second
    // un peu plus tard pour capturer les sections dynamiques (lazy, CMS fetch).
    const raf = requestAnimationFrame(announceReady);
    const t1 = setTimeout(announceReady, 600);
    const t2 = setTimeout(announceReady, 1800);

    const handleMessage = (event) => {
      // Strict origin check : seul le même site peut piloter le preview
      if (event.origin !== window.location.origin) return;
      const data = event.data || {};
      if (!data || typeof data !== 'object') return;

      if (data.type === 'cms:highlight') {
        const ok = highlightSection(data.key, { scroll: data.scroll !== false });
        postToParent({ type: 'cms:highlight-ack', key: data.key, found: ok });
      } else if (data.type === 'cms:list-sections') {
        announceReady();
      } else if (data.type === 'cms:scroll-top') {
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch { window.scrollTo(0, 0); }
      } else if (data.type === 'cms:apply-draft-field' && editMode) {
        applyDraftField(data.path, data.value);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
      if (highlightTimer) {
        clearTimeout(highlightTimer);
        highlightTimer = null;
      }
      document.documentElement.classList.remove('cms-preview-active');
      document.documentElement.classList.remove('cms-edit-active');
    };
  }, []);
}
